'use client'

import { FileText, Briefcase, User, Building2, Hash } from 'lucide-react'
import { SearchHighlight } from './search-highlight'
import type { PromptSearchResult, JobSearchResult, UserSearchResult, TopicSearchResult } from '@/lib/search/types'

const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

export function PromptTypeaheadItem({ item, isActive }: { item: PromptSearchResult; isActive: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive ? 'bg-[var(--color-bg-elevated)]' : ''}`}
      style={font}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10">
        <FileText className="h-4 w-4 text-[var(--color-brand-indigo)]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">{item.title}</p>
        <p className="truncate text-xs text-[var(--color-text-muted)]">
          by {item.displayName || item.usernameRef}
          {item.category && <span> &middot; {item.category}</span>}
        </p>
      </div>
    </div>
  )
}

export function JobTypeaheadItem({ item, isActive }: { item: JobSearchResult; isActive: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive ? 'bg-[var(--color-bg-elevated)]' : ''}`}
      style={font}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
        <Briefcase className="h-4 w-4 text-emerald-500" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">{item.title}</p>
        <p className="truncate text-xs text-[var(--color-text-muted)]">
          {item.companyName}
          {item.locationType && <span> &middot; {item.locationType}</span>}
        </p>
      </div>
    </div>
  )
}

export function UserTypeaheadItem({ item, isActive }: { item: UserSearchResult; isActive: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive ? 'bg-[var(--color-bg-elevated)]' : ''}`}
      style={font}
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${item.accountType === 'company' ? 'bg-emerald-500/10' : 'bg-violet-500/10'}`}>
        {item.accountType === 'company' ? (
          <Building2 className="h-4 w-4 text-emerald-500" />
        ) : (
          <User className="h-4 w-4 text-violet-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
          {item.displayName || item.username}
        </p>
        {item.bio ? (
          <SearchHighlight html={item.headline} className="line-clamp-1 text-xs text-[var(--color-text-muted)]" />
        ) : (
          <p className="text-xs text-[var(--color-text-muted)]">@{item.username}</p>
        )}
      </div>
    </div>
  )
}

export function TopicTypeaheadItem({ item, isActive }: { item: TopicSearchResult; isActive: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive ? 'bg-[var(--color-bg-elevated)]' : ''}`}
      style={font}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10">
        <Hash className="h-4 w-4 text-[var(--color-brand-indigo)]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
          {item.name ?? `#${item.tag}`}
        </p>
        <p className="truncate text-xs text-[var(--color-text-muted)]">
          #{item.tag} &middot; {item.threadCount} thread{item.threadCount === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  )
}
